import React, { createContext, useContext, useState, useEffect } from 'react'

const CartContext = createContext()

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([])
  const [loading, setLoading] = useState(false)

  // Загружаем корзину из localStorage при инициализации
  useEffect(() => {
    loadCartFromStorage()
  }, [])

  // Сохраняем корзину в localStorage при изменении
  useEffect(() => {
    saveCartToStorage()
  }, [cartItems])

  const loadCartFromStorage = () => {
    try {
      const savedCart = localStorage.getItem('NCORE_cart')
      if (savedCart) {
        setCartItems(JSON.parse(savedCart))
      }
    } catch (err) {
      console.error('Ошибка загрузки корзины:', err)
    }
  }

  const saveCartToStorage = () => {
    try {
      localStorage.setItem('NCORE_cart', JSON.stringify(cartItems))
    } catch (err) {
      console.error('Ошибка сохранения корзины:', err)
    }
  }

  // Добавление товара в корзину
  const addToCart = (item, options = {}) => {
    const newItem = {
      id: item.id,
      name: item.name || item.title,
      price: item.price,
      image: item.image || item.poster,
      type: options.type || 'product',
      quantity: options.quantity || 1,
      ...options
    }

    setCartItems(prev => {
      const existingItemIndex = prev.findIndex(
        cartItem => cartItem.id === item.id && cartItem.type === newItem.type
      )

      if (existingItemIndex >= 0) {
        // Если товар уже есть, увеличиваем количество
        const updatedItems = [...prev]
        updatedItems[existingItemIndex].quantity += newItem.quantity
        return updatedItems
      } else {
        // Добавляем новый товар
        return [...prev, newItem]
      }
    })
  }

  // Удаление товара из корзины
  const removeFromCart = (itemId, type = 'product') => {
    setCartItems(prev => 
      prev.filter(item => !(item.id === itemId && item.type === type))
    )
  }

  // Обновление количества товара
  const updateQuantity = (itemId, quantity, type = 'product') => {
    if (quantity <= 0) {
      removeFromCart(itemId, type)
      return
    }

    setCartItems(prev =>
      prev.map(item =>
        item.id === itemId && item.type === type
          ? { ...item, quantity }
          : item
      )
    )
  }

  // Очистка корзины
  const clearCart = () => {
    setCartItems([])
  }

  // Получение общего количества товаров
  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + (item.quantity || 1), 0)
  }

  // Получение общей стоимости
  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => {
      const itemPrice = item.price || 0
      const itemQuantity = item.quantity || 1
      return total + (itemPrice * itemQuantity)
    }, 0)
  }

  // Проверка, есть ли товар в корзине
  const isInCart = (itemId, type = 'product') => {
    return cartItems.some(item => item.id === itemId && item.type === type)
  }

  // Получение товара из корзины
  const getCartItem = (itemId, type = 'product') => {
    return cartItems.find(item => item.id === itemId && item.type === type)
  }

  // Добавление блюда в корзину (для ресторанов)
  const addFoodToCart = (restaurant, dish, options = {}) => {
    const foodItem = {
      id: `${restaurant.id}-${dish.id}`,
      name: dish.name,
      price: dish.price,
      image: dish.image,
      type: 'food',
      quantity: options.quantity || 1,
      restaurant_id: restaurant.id,
      restaurant_name: restaurant.name,
      dish_id: dish.id,
      ...options
    }

    addToCart(foodItem, { type: 'food' })
  }

  // Добавление билета в кинотеатр
  const addMovieTicketToCart = (movie, session, seats, options = {}) => {
    const ticketItem = {
      id: `${movie.id}-${session.id}-${Date.now()}`,
      name: `${movie.title} - ${session.time}`,
      price: session.price * seats.length,
      image: movie.poster,
      type: 'movie',
      quantity: 1,
      movie_id: movie.id,
      session_id: session.id,
      seats: seats,
      ...options
    }

    addToCart(ticketItem, { type: 'movie' })
  }

  // Добавление NCORE IT Услуги
  const addServiceToCart = (service, options = {}) => {
    const serviceItem = {
      id: service.id,
      name: service.name,
      price: service.price,
      image: service.image,
      type: 'service',
      quantity: 1,
      service_id: service.id,
      ...options
    }

    addToCart(serviceItem, { type: 'service' })
  }

  const value = {
    cartItems,
    loading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice,
    isInCart,
    getCartItem,
    addFoodToCart,
    addMovieTicketToCart,
    addServiceToCart
  }

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}